import React, { useState, useRef, useEffect } from 'react';

// --- ARQUITETURA: D-PAD & SWIPE CONTROLLER ---
// - Toque Central: onClick (Abrir Menu)
// - Toque nas Bordas (Quadrantes): onNavigate (Passo a passo)
// - Deslize (Swipe): onNavigate (Fluido)

const CONFIG = {
  SWIPE_THRESHOLD: 15,   // Distância mínima para considerar Swipe
  TAP_DEADZONE: 20,      // Raio central onde o toque é considerado "Clique no Menu"
  RIPPLE_DURATION: 600,
  DEBOUNCE_TIME: 150     // Debounce rápido para permitir toques repetidos
};

interface MainButtonProps {
  isOpen: boolean;
  onClick: () => void;
  onNavigate: (direction: 'up' | 'down' | 'left' | 'right') => void;
}

interface Ripple {
  x: number;
  y: number;
  id: number;
}

export const MainButton: React.FC<MainButtonProps> = ({ isOpen, onClick, onNavigate }) => {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const [activeDirection, setActiveDirection] = useState<'up' | 'down' | 'left' | 'right' | null>(null);
  
  const buttonRef = useRef<HTMLButtonElement>(null);
  const startPosRef = useRef<{x: number, y: number} | null>(null);
  const isDraggingRef = useRef(false);
  const lastActionTimeRef = useRef(0);

  // 1. Início (Touch/Mouse Down)
  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    
    startPosRef.current = { x: e.clientX, y: e.clientY };
    isDraggingRef.current = false;

    // Efeito Visual Ripple
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const newRipple = { x, y, id: Date.now() };
    setRipples((prev) => [...prev, newRipple]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
    }, CONFIG.RIPPLE_DURATION);
  };

  // 2. Movimento (Swipe em tempo real)
  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!startPosRef.current) return;

    const deltaX = e.clientX - startPosRef.current.x;
    const deltaY = e.clientY - startPosRef.current.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Se moveu o suficiente, consideramos SWIPE/DRAG
    if (distance > CONFIG.SWIPE_THRESHOLD) {
      isDraggingRef.current = true;
      
      const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
      let direction: 'up' | 'down' | 'left' | 'right' | null = null;
      
      if (angle > -45 && angle <= 45) direction = 'right';
      else if (angle > 45 && angle <= 135) direction = 'down';
      else if (angle > 135 || angle <= -135) direction = 'left';
      else direction = 'up';

      // Feedback visual apenas, a ação ocorre no gesto completo ou contínua se desejado
      // Para navegação fluida, podemos chamar onNavigate aqui com Throttle, 
      // mas vamos manter simples: Visual Update agora, Açao no End ou Throttle
      if (direction !== activeDirection) {
        setActiveDirection(direction);
      }
    }
  };

  // 3. Fim (Touch/Mouse Up) - Onde a mágica acontece
  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.releasePointerCapture(e.pointerId);

    if (!buttonRef.current || !startPosRef.current) return;

    const now = Date.now();
    if (now - lastActionTimeRef.current < CONFIG.DEBOUNCE_TIME) {
       setActiveDirection(null);
       return; 
    }
    lastActionTimeRef.current = now;

    const rect = buttonRef.current.getBoundingClientRect();
    
    // A. Lógica de SWIPE (Arrasto)
    if (isDraggingRef.current && activeDirection) {
      onNavigate(activeDirection); // Confirma a navegação do swipe
    } 
    // B. Lógica de TAP (Toque Rápido)
    else {
      // Onde foi o toque relativo ao centro do botão?
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const clickX = e.clientX;
      const clickY = e.clientY;
      
      const deltaX = clickX - centerX;
      const deltaY = clickY - centerY;
      const distanceFromCenter = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Zona Morta Central -> CLIQUE DO MENU
      if (distanceFromCenter <= CONFIG.TAP_DEADZONE) {
        onClick();
      } 
      // Bordas -> D-PAD VIRTUAL
      else {
        const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
        let direction: 'up' | 'down' | 'left' | 'right' | null = null;
        
        if (angle > -45 && angle <= 45) direction = 'right';
        else if (angle > 45 && angle <= 135) direction = 'down';
        else if (angle > 135 || angle <= -135) direction = 'left';
        else direction = 'up';
        
        if (direction) {
          // Visual rápido
          setActiveDirection(direction);
          setTimeout(() => setActiveDirection(null), 200);
          onNavigate(direction);
        }
      }
    }

    // Reset geral
    startPosRef.current = null;
    isDraggingRef.current = false;
    if (activeDirection && isDraggingRef.current) {
         // Se foi swipe, limpa visual logo
         setTimeout(() => setActiveDirection(null), 200);
    } else if (!isDraggingRef.current && !activeDirection) {
         // Se foi clique central
         setActiveDirection(null);
    }
  };

  return (
    <div className="relative group cursor-pointer z-50 select-none touch-none">
      {/* Ripple/Pulse Effects - Ambientais */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[83px] h-[83px] rounded-full border border-white/10 opacity-60 transition-transform duration-500 ${isOpen ? 'scale-110 opacity-0' : 'scale-100 group-hover:scale-110 group-hover:opacity-80'}`} />
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[98px] h-[98px] rounded-full border border-white/5 opacity-30 transition-transform duration-700 ${isOpen ? 'scale-125 opacity-0' : 'scale-90 group-hover:scale-105'}`} />

      {/* The Button Itself */}
      <button
        ref={buttonRef}
        type="button"
        style={{ touchAction: 'none' }} // ESSENCIAL PARA MOBILE
        
        // Eventos Unificados (Pointer)
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => { setActiveDirection(null); isDraggingRef.current = false; }}
        
        // Supressão de Eventos Nativos
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onContextMenu={(e) => e.preventDefault()}
        
        aria-label={isOpen ? "Close Menu" : "Open Menu"}
        className={`relative transition-all duration-500 ease-out transform active:scale-95 overflow-hidden flex items-center justify-center touch-none select-none
            ${isOpen 
                ? 'w-[96px] h-[96px] rounded-full sphere-active-gradient border border-white/30 shadow-[0_20px_40px_rgba(0,0,0,0.2)] backdrop-blur-md' 
                : 'w-[92px] h-[92px] rounded-full border border-white/10 backdrop-blur-md sphere-gradient shadow-glass-convex group-hover:shadow-glass-convex-hover sphere-outer-glow'
            }
        `}
      >
        {/* Ripples Internos */}
        {ripples.map((ripple) => (
          <span
            key={ripple.id}
            className="absolute w-[160px] h-[160px] bg-white/30 rounded-full animate-ripple pointer-events-none"
            style={{ left: ripple.x, top: ripple.y }}
          />
        ))}

        {/* LOGO */}
        <div 
            className={`absolute inset-0 flex flex-col items-center justify-center pointer-events-none transition-all duration-500 ease-in-out z-10
            ${isOpen ? 'opacity-20 grayscale blur-[0.5px]' : 'opacity-100'}`}
        >
            <div className="flex flex-col items-center leading-none select-none transform translate-y-0 scale-90">
                <span 
                    className="font-extrabold text-[13px] tracking-tight text-[#4fc3f7] drop-shadow-[0_1px_0_rgba(0,0,0,0.6)]"
                    style={{ 
                        fontFamily: "'Plus Jakarta Sans', sans-serif", 
                        textShadow: '0px 0px 3px rgba(0,0,0,0.5)',
                        WebkitTextStroke: '0.5px rgba(0,0,0,0.4)'
                    }}
                >
                    NOSSA
                </span>
                <span 
                    className="font-extrabold text-[16px] tracking-tight text-[#ffca28] -mt-[3px] drop-shadow-[0_1px_0_rgba(0,0,0,0.6)]"
                    style={{ 
                        fontFamily: "'Plus Jakarta Sans', sans-serif", 
                        textShadow: '0px 0px 3px rgba(0,0,0,0.5)',
                        WebkitTextStroke: '0.5px rgba(0,0,0,0.4)'
                    }}
                >
                    PESCA
                </span>
            </div>
        </div>

        {/* Glossy Highlights */}
        <div className={`absolute top-[10%] left-[15%] w-[40%] h-[25%] bg-gradient-to-br from-white/60 to-transparent blur-[5px] rounded-[50%] -rotate-45 opacity-60 transition-opacity z-30 ${isOpen ? 'opacity-30' : 'opacity-60'}`} />
        <div className={`absolute bottom-[5%] left-[10%] right-[10%] h-[30%] bg-white/5 blur-[8px] rounded-[50%] opacity-30 z-30`} />

        {/* Setas Direcionais (Indicadores Visuais) */}
        <span 
            className={`material-symbols-outlined absolute top-[2px] left-1/2 -translate-x-1/2 z-40 text-[20px] transition-all duration-200 pointer-events-none
            ${isOpen ? 'opacity-0' : ''}
            ${activeDirection === 'up' ? 'text-[#4fc3f7] scale-125 drop-shadow-[0_0_8px_#4fc3f7] opacity-100' : 'text-white/40 opacity-50'}
            `}
        >
            keyboard_arrow_up
        </span>
        <span 
            className={`material-symbols-outlined absolute bottom-[2px] left-1/2 -translate-x-1/2 z-40 text-[20px] transition-all duration-200 pointer-events-none
            ${isOpen ? 'opacity-0' : ''}
            ${activeDirection === 'down' ? 'text-[#4fc3f7] scale-125 drop-shadow-[0_0_8px_#4fc3f7] opacity-100' : 'text-white/40 opacity-50'}
            `}
        >
            keyboard_arrow_down
        </span>
        <span 
            className={`material-symbols-outlined absolute left-[2px] top-1/2 -translate-y-1/2 z-40 text-[20px] transition-all duration-200 pointer-events-none
            ${isOpen ? 'opacity-0' : ''}
            ${activeDirection === 'left' ? 'text-[#4fc3f7] scale-125 drop-shadow-[0_0_8px_#4fc3f7] opacity-100' : 'text-white/40 opacity-50'}
            `}
        >
            keyboard_arrow_left
        </span>
        <span 
            className={`material-symbols-outlined absolute right-[2px] top-1/2 -translate-y-1/2 z-40 text-[20px] transition-all duration-200 pointer-events-none
            ${isOpen ? 'opacity-0' : ''}
            ${activeDirection === 'right' ? 'text-[#4fc3f7] scale-125 drop-shadow-[0_0_8px_#4fc3f7] opacity-100' : 'text-white/40 opacity-50'}
            `}
        >
            keyboard_arrow_right
        </span>

        {/* Ícone Fechar */}
        <span 
            className={`material-symbols-outlined absolute z-50 text-[32px] text-red-500 drop-shadow-[0_0_10px_rgba(220,38,38,0.6)] transition-all duration-300 pointer-events-none
            ${isOpen ? 'opacity-100 rotate-0 scale-100 delay-100' : 'opacity-0 -rotate-90 scale-50'}`}
        >
            close
        </span>
      </button>
    </div>
  );
};